import { Alert, Button, Fieldset, Group, Select, Stack, Tabs, Text, TextInput } from '@mantine/core';
import { useState } from 'react';
import HelpPopover from '../../HelpPopover/HelpPopover';
import KeyValueMapper from '../../KeyValueMapper';
import DomainSecretSelect from '../../Settings/DomainSecretSelect';
import SecretCreateModal from '../../Settings/SecretCreateModal';
import ArgumentsTable from './ArgumentsTable';
import PlaceholderInput from './PlaceholderInput';
import { HELP } from './workspaceHelp';

const AUTH_IN = [
  { value: 'header', label: 'Header' },
  { value: 'query', label: 'Query parameter' },
];

const AuthPanel = ({ form, setField, errors, readOnly, accountId, domainId, onError }) => {
  const [creating, setCreating] = useState(false);
  // A secret created here is invisible to the picker until its /secrets read runs again.
  const [secretKey, setSecretKey] = useState(0);

  return (
    <Fieldset variant="unstyled" disabled={readOnly}>
      <Stack gap="xs">
        <Group gap="xs" align="flex-end" wrap="nowrap">
          <DomainSecretSelect
            label="Secret"
            placeholder="No secret"
            accountId={accountId}
            domainId={domainId}
            refreshKey={secretKey}
            value={form.domainSecretId}
            onChange={(value) => setField('domainSecretId', value)}
            onError={onError}
          />
          <Button variant="subtle" size="xs" onClick={() => setCreating(true)}>
            New secret
          </Button>
          <HelpPopover {...HELP.auth} />
        </Group>
        {form.domainSecretId && (
          <Group gap="xs" grow align="flex-start">
            <Select
              label="Add to"
              size="xs"
              data={AUTH_IN}
              allowDeselect={false}
              value={form.authIn}
              onChange={(value) => setField('authIn', value || 'header')}
            />
            {form.authIn === 'query' ? (
              <TextInput
                label="Parameter"
                size="xs"
                placeholder="api_key"
                error={errors.authParam}
                value={form.authParam}
                onChange={(event) => setField('authParam', event.target.value)}
              />
            ) : (
              <>
                <TextInput
                  label="Header"
                  size="xs"
                  error={errors.authHeader}
                  value={form.authHeader}
                  onChange={(event) => setField('authHeader', event.target.value)}
                />
                <TextInput
                  label="Prefix"
                  size="xs"
                  value={form.authPrefix}
                  onChange={(event) => setField('authPrefix', event.target.value)}
                />
              </>
            )}
          </Group>
        )}
        <Text size="xs" c="dimmed">
          Write-only, and masked in every test.
        </Text>
      </Stack>
      <SecretCreateModal
        opened={creating}
        onClose={() => setCreating(false)}
        onCreated={(secret) => {
          setField('domainSecretId', secret.id);
          setSecretKey((key) => key + 1);
        }}
        baseUrl={`/api/admin/accounts/${accountId}/domains/${domainId}/secrets`}
      />
    </Fieldset>
  );
};

const RequestStep = ({ ws }) => {
  const { form, setField, setToolField, errors, readOnly, argNames, shopperNames, testValues, setTestValue } = ws;
  const toolErrors = errors.tool || {};

  return (
    <Tabs value={ws.sub} onChange={(value) => ws.setSub(value || 'args')} keepMounted={false}>
      <Tabs.List>
        <Tabs.Tab value="args">{ws.isTool ? `Arguments ${form.tool.argRows.length || ''}` : 'Test values'}</Tabs.Tab>
        <Tabs.Tab value="auth">Auth</Tabs.Tab>
        <Tabs.Tab value="headers">Headers {form.headerRows.length || ''}</Tabs.Tab>
        <Tabs.Tab value="body">Body</Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="args" pt="xs">
        <ArgumentsTable
          tool={form.tool}
          setToolField={setToolField}
          shopperNames={shopperNames}
          platformNames={ws.isTool ? [] : ws.platformNames}
          declare={ws.isTool}
          testValues={testValues}
          setTestValue={setTestValue}
          errors={toolErrors}
          readOnly={readOnly}
        />
        {ws.noIdentity && (
          <Alert color="warning" variant="light" mt="xs" py={6}>
            This request uses no shopper identity the server can fill in (email, phone or profileId), so every shopper
            would get the same answer.
          </Alert>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="auth" pt="xs">
        <AuthPanel
          form={form}
          setField={setField}
          errors={errors}
          readOnly={readOnly}
          accountId={ws.accountId}
          domainId={ws.domainId}
          onError={ws.onSecretsError}
        />
      </Tabs.Panel>

      <Tabs.Panel value="headers" pt="xs">
        <Fieldset variant="unstyled" disabled={readOnly}>
          <KeyValueMapper
            rows={form.headerRows}
            setRows={(rows) => setField('headerRows', rows)}
            keyLabel="Header"
            valueLabel="Header value"
            keyPlaceholder="Accept"
            valuePlaceholder="application/json"
            addLabel="Add header"
          />
        </Fieldset>
        {errors.headers && (
          <Text size="xs" c="red">
            {errors.headers}
          </Text>
        )}
        <Text size="xs" c="dimmed">
          The token is not a header here: pick a secret under Auth.
        </Text>
      </Tabs.Panel>

      <Tabs.Panel value="body" pt="xs">
        {form.method === 'POST' ? (
          <PlaceholderInput
            multiline
            aria-label="Body"
            autosize
            minRows={4}
            placeholder={'{"query": "{{args.query}}"}'}
            readOnly={readOnly}
            error={errors.body}
            value={form.body}
            onChange={(value) => setField('body', value)}
            argNames={argNames}
            testValues={testValues}
          />
        ) : (
          <Text size="xs" c="dimmed">
            Only for POST. Switch the method to write a JSON body; placeholders work the same way.
          </Text>
        )}
      </Tabs.Panel>
    </Tabs>
  );
};

export default RequestStep;
